import { html, nothing } from "lit";
import type { GatewayHelloOk } from "../gateway.ts";
import type { UiSettings } from "../storage.ts";
import { formatAgo, formatDurationMs } from "../format.ts";
import { formatNextRun } from "../presenter.ts";

export type OverviewProps = {
  connected: boolean;
  hello: GatewayHelloOk | null;
  settings: UiSettings;
  password: string;
  lastError: string | null;
  presenceCount: number;
  sessionsCount: number | null;
  cronEnabled: boolean | null;
  cronNext: number | null;
  lastChannelsRefresh: number | null;
  googleDriveStatus: {
    configured: boolean;
    clientEmail?: string;
    storagePath?: string;
    viaAccessTokenEnv?: boolean;
  } | null;
  googleDriveBusy: boolean;
  googleDriveMessage: string | null;
  googleDriveJsonDraft: string;
  onSettingsChange: (next: UiSettings) => void;
  onPasswordChange: (next: string) => void;
  onSessionKeyChange: (next: string) => void;
  onConnect: () => void;
  onRefresh: () => void;
  onGoogleDriveJsonDraftChange: (next: string) => void;
  onGoogleDriveSave: () => void;
  onGoogleDriveClear: () => void;
  onGoogleDriveTest: () => void;
};

export function renderOverview(props: OverviewProps) {
  const snapshot = props.hello?.snapshot as
    | { uptimeMs?: number; policy?: { tickIntervalMs?: number } }
    | undefined;
  const uptime = snapshot?.uptimeMs ? formatDurationMs(snapshot.uptimeMs) : "n/a";
  const tick = snapshot?.policy?.tickIntervalMs ? `${snapshot.policy.tickIntervalMs}ms` : "n/a";
  const authHint = (() => {
    if (props.connected || !props.lastError) {
      return null;
    }
    const lower = props.lastError.toLowerCase();
    const authFailed = lower.includes("unauthorized") || lower.includes("connect failed");
    if (!authFailed) {
      return null;
    }
    const hasToken = Boolean(props.settings.token.trim());
    const hasPassword = Boolean(props.password.trim());
    if (!hasToken && !hasPassword) {
      return html`
        <div class="muted" style="margin-top: 8px">
          This gateway requires auth. Add a token or password, then click Connect.
          <div style="margin-top: 6px">
            <span class="mono">jaisiu dashboard --no-open</span> → tokenized URL<br />
            <span class="mono">jaisiu doctor --generate-gateway-token</span> → set token
          </div>
          <div style="margin-top: 6px">
            <a
              class="session-link"
              href="https://docs.jaisiu.ai/web/dashboard"
              target="_blank"
              rel="noreferrer"
              title="Control UI auth docs (opens in new tab)"
              >Docs: Control UI auth</a
            >
          </div>
        </div>
      `;
    }
    return html`
      <div class="muted" style="margin-top: 8px">
        Auth failed. Re-copy a tokenized URL with
        <span class="mono">jaisiu dashboard --no-open</span>, or update the token, then click Connect.
        <div style="margin-top: 6px">
          <a
            class="session-link"
            href="https://docs.jaisiu.ai/web/dashboard"
            target="_blank"
            rel="noreferrer"
            title="Control UI auth docs (opens in new tab)"
            >Docs: Control UI auth</a
          >
        </div>
      </div>
    `;
  })();
  const insecureContextHint = (() => {
    if (props.connected || !props.lastError) {
      return null;
    }
    const isSecureContext = typeof window !== "undefined" ? window.isSecureContext : true;
    if (isSecureContext) {
      return null;
    }
    const lower = props.lastError.toLowerCase();
    if (!lower.includes("secure context") && !lower.includes("device identity required")) {
      return null;
    }
    return html`
      <div class="muted" style="margin-top: 8px">
        This page is HTTP, so the browser blocks device identity. Use HTTPS (Tailscale Serve) or open
        <span class="mono">http://127.0.0.1:18789</span> on the gateway host.
        <div style="margin-top: 6px">
          If you must stay on HTTP, set
          <span class="mono">gateway.controlUi.allowInsecureAuth: true</span> (token-only).
        </div>
        <div style="margin-top: 6px">
          <a
            class="session-link"
            href="https://docs.jaisiu.ai/gateway/tailscale"
            target="_blank"
            rel="noreferrer"
            title="Tailscale Serve docs (opens in new tab)"
            >Docs: Tailscale Serve</a
          >
          <span class="muted"> · </span>
          <a
            class="session-link"
            href="https://docs.jaisiu.ai/web/control-ui#insecure-http"
            target="_blank"
            rel="noreferrer"
            title="Insecure HTTP docs (opens in new tab)"
            >Docs: Insecure HTTP</a
          >
        </div>
      </div>
    `;
  })();

  return html`
    <section class="grid grid-cols-2">
      <div class="card" data-testid="auth-required">
        <div class="card-title">Gateway Access</div>
        <div class="card-sub">Where the dashboard connects and how it authenticates.</div>
        <div class="form-grid" style="margin-top: 16px;">
          <label class="field">
            <span>WebSocket URL</span>
            <input
              .value=${props.settings.gatewayUrl}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSettingsChange({ ...props.settings, gatewayUrl: v });
              }}
              placeholder="ws://100.x.y.z:18789"
            />
          </label>
          <label class="field">
            <span>Gateway Token</span>
            <input
              .value=${props.settings.token}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSettingsChange({ ...props.settings, token: v });
              }}
              placeholder="OPENCLAW_GATEWAY_TOKEN"
            />
          </label>
          <label class="field">
            <span>Password (not stored)</span>
            <input
              type="password"
              .value=${props.password}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onPasswordChange(v);
              }}
              placeholder="system or shared password"
            />
          </label>
          <label class="field">
            <span>Default Session Key</span>
            <input
              .value=${props.settings.sessionKey}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                props.onSessionKeyChange(v);
              }}
            />
          </label>
        </div>
        <div class="row" style="margin-top: 14px;">
          <button class="btn" @click=${() => props.onConnect()}>Connect</button>
          <button class="btn" @click=${() => props.onRefresh()}>Refresh</button>
          <span class="muted">Click Connect to apply connection changes.</span>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Snapshot</div>
        <div class="card-sub">Latest gateway handshake information.</div>
        <div class="stat-grid" style="margin-top: 16px;">
          <div class="stat">
            <div class="stat-label">Status</div>
            <div class="stat-value ${props.connected ? "ok" : "warn"}">
              ${props.connected ? "Connected" : "Disconnected"}
            </div>
          </div>
          <div class="stat">
            <div class="stat-label">Uptime</div>
            <div class="stat-value">${uptime}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Tick Interval</div>
            <div class="stat-value">${tick}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Last Channels Refresh</div>
            <div class="stat-value">
              ${props.lastChannelsRefresh ? formatAgo(props.lastChannelsRefresh) : "n/a"}
            </div>
          </div>
        </div>
        ${
          props.lastError
            ? html`<div class="callout danger" style="margin-top: 14px;">
              <div>${props.lastError}</div>
              ${authHint ?? ""}
              ${insecureContextHint ?? ""}
            </div>`
            : html`
                <div class="callout" style="margin-top: 14px">
                  Use Channels to link WhatsApp, Telegram, Discord, Signal, or iMessage.
                </div>
              `
        }
      </div>
    </section>

    <section class="card" style="margin-top: 18px;">
      <div class="card-title">Google Drive (service account)</div>
      <div class="card-sub">
        Paste your GCP service account JSON and click <strong>Save key</strong>. The file is created on the
        gateway only after a successful save (mode <span class="mono">0600</span>). It is <strong>not</strong> in
        your project directory — it lives under
        <span class="mono">$STATE_DIR/credentials/google-drive-service-account.json</span> on the machine (or
        container) running the gateway. <span class="mono">$STATE_DIR</span> comes from
        <span class="mono">JAISIU_STATE_DIR</span> / <span class="mono">OPENCLAW_STATE_DIR</span>, or defaults to
        an existing <span class="mono">~/.jaisiu</span> / <span class="mono">~/.openclaw</span> / legacy state dir.
        You can override only the credentials directory with <span class="mono">JAISIU_OAUTH_DIR</span> /
        <span class="mono">OPENCLAW_OAUTH_DIR</span>. When connected, the exact resolved path appears below.
        The agent and harness mint short-lived tokens automatically. Share folders with the service account
        email so it can see your files.
      </div>
      <div class="muted" style="margin-top: 12px;">
        Status:
        ${
          props.googleDriveStatus?.configured
            ? html`<strong
                >${
                  props.googleDriveStatus.viaAccessTokenEnv
                    ? "GOOGLE_DRIVE_ACCESS_TOKEN (env)"
                    : (props.googleDriveStatus.clientEmail ?? "configured")
                }</strong
              >`
            : html`
                <span>not configured</span>
              `
        }
        ${
          !props.connected
            ? html`
                <span> · Connect to the gateway to manage.</span>
              `
            : ""
        }
      </div>
      ${
        props.connected && props.googleDriveStatus?.storagePath
          ? html`<div class="muted mono" style="margin-top: 10px; word-break: break-all;">
              Resolved gateway path:
              ${props.googleDriveStatus.storagePath}
            </div>`
          : nothing
      }
      <label class="field" style="margin-top: 14px; display: block;">
        <span>Service account JSON</span>
        <textarea
          rows="6"
          style="width: 100%; font-family: var(--font-mono, monospace); font-size: 12px;"
          .value=${props.googleDriveJsonDraft}
          placeholder='{"type":"service_account",...}'
          ?disabled=${!props.connected || props.googleDriveBusy}
          @input=${(e: Event) => {
            props.onGoogleDriveJsonDraftChange((e.target as HTMLTextAreaElement).value);
          }}
        ></textarea>
      </label>
      <div class="row" style="margin-top: 12px; flex-wrap: wrap; gap: 8px;">
        <button
          class="btn primary"
          ?disabled=${!props.connected || props.googleDriveBusy}
          @click=${() => props.onGoogleDriveSave()}
        >
          Save key
        </button>
        <button
          class="btn"
          ?disabled=${!props.connected || props.googleDriveBusy || !props.googleDriveStatus?.configured}
          @click=${() => props.onGoogleDriveClear()}
        >
          Remove key
        </button>
        <button
          class="btn"
          ?disabled=${!props.connected || props.googleDriveBusy}
          @click=${() => props.onGoogleDriveTest()}
        >
          Test Drive API
        </button>
      </div>
      ${
        props.googleDriveMessage
          ? html`<div class="callout" style="margin-top: 12px;">
              <div style="white-space: pre-wrap; word-break: break-word;">${props.googleDriveMessage}</div>
            </div>`
          : nothing
      }
    </section>

    <section class="grid grid-cols-3" style="margin-top: 18px;">
      <div class="card stat-card">
        <div class="stat-label">Instances</div>
        <div class="stat-value">${props.presenceCount}</div>
        <div class="muted">Presence beacons in the last 5 minutes.</div>
      </div>
      <div class="card stat-card">
        <div class="stat-label">Sessions</div>
        <div class="stat-value">${props.sessionsCount ?? "n/a"}</div>
        <div class="muted">Recent session keys tracked by the gateway.</div>
      </div>
      <div class="card stat-card">
        <div class="stat-label">Cron</div>
        <div class="stat-value">
          ${props.cronEnabled == null ? "n/a" : props.cronEnabled ? "Enabled" : "Disabled"}
        </div>
        <div class="muted">Next wake ${formatNextRun(props.cronNext)}</div>
      </div>
    </section>

    <section class="card" style="margin-top: 18px;">
      <div class="card-title">Notes</div>
      <div class="card-sub">Quick reminders for remote control setups.</div>
      <div class="note-grid" style="margin-top: 14px;">
        <div>
          <div class="note-title">Tailscale serve</div>
          <div class="muted">
            Prefer serve mode to keep the gateway on loopback with tailnet auth.
          </div>
        </div>
        <div>
          <div class="note-title">Session hygiene</div>
          <div class="muted">Use /new or sessions.patch to reset context.</div>
        </div>
        <div>
          <div class="note-title">Cron reminders</div>
          <div class="muted">Use isolated sessions for recurring runs.</div>
        </div>
      </div>
    </section>
  `;
}
